import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import LoginLogo from '../../assets/svg/login_logo.svg';
import left_arrow from '../../assets/svg/left_arrow.svg';
import { useNavigate } from 'react-router-dom';
import DP from '../../assets/svg/login_logo.svg';
import Avatar from 'react-avatar';
import { getUserById } from '../../../builder/services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import NotificationBell from './NotificationBell';

/**
 * App chrome header.
 *
 * Back button convention:
 * - Prefer passing `onBack` from `useSmartBack({ fallback })` so list filters in
 *   `returnTo` (location.state / ?returnTo=) are restored.
 * - Without `onBack`, falls back to `navigate(-1)` (legacy).
 */
const Header = ({ data, onBack, backArrow, onProjectSelect, showProjectSelector = false, children }) => {
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const navigate = useNavigate();
  // Project selector dropdown state/refs
  const projectTriggerRef = useRef(null);
  const projectDropdownRef = useRef(null);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const userId = Cookies.get('userId');
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);

  // Lite profile only (name/avatar) — do not share cache with UserDetails full
  const { data: user } = useQuery({
    queryKey: ['userLite', userId],
    queryFn: () => getUserById(userId),
    enabled: !!userId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false
  });

  console.log({ user });

  // Initialize first project when projects are loaded
  useEffect(() => {
    if (projects?.length > 0 && !selectedProject) {
      setCurrentProjectIndex(0);
      setSelectedProject(projects[0]);
      if (onProjectSelect) {
        onProjectSelect(projects[0]);
      }
    }
  }, [projects, selectedProject, onProjectSelect]);

  // Handle project selection
  const handleProjectSelect = (project, index) => {
    setCurrentProjectIndex(index);
    setSelectedProject(project);
    setShowProjectDropdown(false);
    if (onProjectSelect) {
      onProjectSelect(project);
    }
  };

  // Helpers for previous/next project navigation
  const goToPreviousProject = () => {
    if (!projects || projects.length === 0) return;
    const newIndex = currentProjectIndex === 0 ? projects.length - 1 : currentProjectIndex - 1;
    handleProjectSelect(projects[newIndex], newIndex);
  };

  const goToNextProject = () => {
    if (!projects || projects.length === 0) return;
    const newIndex = currentProjectIndex === projects.length - 1 ? 0 : currentProjectIndex + 1;
    handleProjectSelect(projects[newIndex], newIndex);
  };

  // Calculate dropdown position for project selector
  const updateProjectDropdownPosition = () => {
    if (!projectTriggerRef.current) return;

    const rect = projectTriggerRef.current.getBoundingClientRect();
    // Use viewport coordinates for a fixed-position portal
    let top = rect.bottom + 4;
    let left = rect.left;

    const dropdownMaxHeight = 240; // 15rem
    if (top + dropdownMaxHeight > window.innerHeight) {
      top = rect.top - dropdownMaxHeight - 4;
    }

    setDropdownPosition({ top, left, width: rect.width });
  };

  // Close project dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        projectDropdownRef.current &&
        !projectDropdownRef.current.contains(event.target) &&
        projectTriggerRef.current &&
        !projectTriggerRef.current.contains(event.target)
      ) {
        setShowProjectDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Keep dropdown aligned on scroll/resize
  useEffect(() => {
    let timeoutId = null;
    const handleScrollOrResize = () => {
      if (!showProjectDropdown) return;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => updateProjectDropdownPosition(), 10);
    };

    if (showProjectDropdown) {
      updateProjectDropdownPosition();
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }
  }, [showProjectDropdown]);

  return (
    <div className="relative flex w-full justify-between items-center px-6 py-4 bg-white border-b-2 z-10">
      <div className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
        <div className="flex shrink-0 items-center gap-3 min-w-0">
          {backArrow && (
            <img
              className="inline-block h-8 w-8 shrink-0 rounded-full ring-3 ring-white cursor-pointer hover:scale-105 transition-all duration-300"
              src={left_arrow}
              alt="Logo_image"
              onClick={onBack ? onBack : () => navigate(-1)}
            />
          )}

          {data?.name ? (
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-lg font-semibold leading-tight text-stone-900">{data.name}</span>
              {data?.subtitle ? (
                <span className="text-sm font-normal leading-snug text-gray-500">{data.subtitle}</span>
              ) : null}
            </div>
          ) : null}
        </div>

        {children && (
          <div className="min-w-0 flex-1 overflow-hidden">
            {children}
          </div>
        )}
      </div>

      <div className="flex shrink-0 gap-4 relative">
        {/* Project Navigation (inline) */}
        {showProjectSelector && (
          <div className="flex items-center gap-2 p-1 rounded-lg bg-white">
            <button
              type="button"
              onClick={goToPreviousProject}
              disabled={!projects || projects.length <= 1}
              className="flex items-center justify-center w-8 h-9 bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-md">
              <svg
                className="w-4 h-4 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              ref={projectTriggerRef}
              type="button"
              onClick={() => setShowProjectDropdown(!showProjectDropdown)}
              className="flex items-center gap-2 px-4 py-[0.3rem] bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors min-w-[10rem]">
              <span className="font-medium text-gray-900 text-sm whitespace-nowrap text-center w-full">
                {selectedProject?.name || projects?.[currentProjectIndex]?.name || 'Loading...'}
              </span>
            </button>

            <button
              type="button"
              onClick={goToNextProject}
              disabled={!projects || projects?.length <= 1}
              className="flex items-center justify-center w-8 h-9 bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-md">
              <svg
                className="w-4 h-4 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}

        <NotificationBell />

        {showProjectDropdown &&
          projects &&
          projects?.length > 0 &&
          ReactDOM.createPortal(
            <div
              ref={projectDropdownRef}
              className="fixed bg-white border border-gray-200 rounded-md shadow-lg z-[10000] max-h-60 overflow-y-auto"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width
              }}>
              {projects?.map((project, index) => (
                <button
                  key={project.id || index}
                  type="button"
                  onClick={() => handleProjectSelect(project, index)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                    index === currentProjectIndex
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-gray-700'
                  }`}>
                  {project.name}
                </button>
              ))}
            </div>,
            document.body
          )}

        <button
          type="button"
          onClick={() => navigate('/builder/profile')}
          className="cursor-pointer rounded-full focus:outline-none focus:ring-2 focus:ring-[#49A4AB] focus:ring-offset-1">
          <Avatar
            name={user?.name || 'Anonymous'}
            size="30"
            round={true}
            maxInitials={2}
            color="#49A4AB"
          />
        </button>
      </div>
    </div>
  );
};

export default Header;
